import { useState, MouseEvent, FC, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import queryString from "query-string";
import {
  Container,
  Typography,
  Stack,
  TextField,
  Button,
  Link,
  FormControl,
  OutlinedInput,
  InputLabel,
  InputAdornment,
  CircularProgress,
  IconButton,
  Unstable_Grid2 as Grid,
  Backdrop,
  Box,
  Divider,
} from "@mui/material";
import { Eye, EyeSlash } from "react-bootstrap-icons";
import Snackbar from "../../../components/Snackbar";
import Modal from "../../../components/Modal";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useServerUrl } from "../../../stores/server-url";
import { useAuth } from "../../../contexts/AuthContext/AuthContext";
import { useModal } from "../../../contexts/ModalContext";
import Inactive from "../../private/Inactive";

interface AuthProps {
  e2eAuth?: boolean;
}

const ForgotModal = () => (
  <Box sx={{ p: 3 }}>
    <Typography variant="body1" sx={{ mb: 2 }}>
      Caso tenha esquecido a sua senha, por favor, acesse o link abaixo e clique
      em "Esqueci Minha Senha":
    </Typography>
    <Button
      fullWidth
      variant="contained"
      onClick={() => {
        window.open(
          "https://socio-americadenatal.futebolcard.com/forgot-my-password",
        );
      }}
    >
      Clique aqui para redefinir sua senha.
    </Button>
  </Box>
);

const Auth: FC<AuthProps> = ({ e2eAuth }) => {
  const navigate = useNavigate();
  const modal = useModal();
  const location = useLocation();
  const snackbar = useSnackbar();
  const { redirect } = queryString.parse(window.location.search);
  const auth = useAuth();
  const serverUrl = useServerUrl((state) => state.url);
  const [progress, setProgress] = useState(false);
  const [backdrop, setBackdrop] = useState(true);
  const [login, setLogin] = useState(e2eAuth);
  const [showPassword, setShowPassword] = useState(false);
  const [isNonMemberBlockedToBuy, SetIsNonMemberBlockedToBuy] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  async function onSubmit(event: any) {
    event.preventDefault();

    const username = event.target.username.value;
    const password = event.target.password.value;
    const payload: {
      username: string;
      password: string;
      idSubscriber?: number;
      user?: object;
    } = { username, password };

    if (username === "") {
      snackbar?.open("missing-user");
      event.target.username.focus();
      return;
    }

    if (password === "") {
      snackbar?.open("missing-password");
      event.target.password.focus();
      return;
    }

    setProgress(true);
    const urlTeam = !e2eAuth ? "/signin/team" : "/signin";
    await fetch(`${serverUrl}/authentication${urlTeam}`, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json;charset=UTF-8",
      },
    })
      .then((response): any => {
        if (response.status !== 200) {
          snackbar?.open("invalid-credentials");
          if (!e2eAuth) {
            modal?.open("Senha incorreta", <ForgotModal />);
          }
          return;
        }
        return response.json();
      })
      .then((data: any) => {
        if (data) {
          localStorage.setItem("userId", data.id);
          localStorage.setItem("username", data.username);
          localStorage.setItem(
            "plan",
            data.plan ? JSON.stringify(data.plan) : "",
          );
          localStorage.setItem("token", data.accessToken);

          if (data.isBlockedToBuy) {
            modal?.open(
              "Comunicado",
              <Inactive
                isBlockedToBuy
                isBlockedToBuyMessage={data.isBlockedToBuyMessage || null}
              />,
            );
          } else {
            auth?.login({ ...data, userId: data.id });
            navigate(!redirect ? "/showcase" : "/shipping");
          }
        }
      })
      .catch((error) => {
        snackbar?.open("invalid-credentials", "error");
        if (!e2eAuth) {
          modal?.open("Senha incorreta", <ForgotModal />);
        }
      });

    setProgress(false);
  }

  const handleBlockedToBuy = async () => {
    await fetch(`${serverUrl}/campaign`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json;charset=UTF-8",
      },
    })
      .then((response): any => {
        return response.json();
      })
      .then((data) => {
        SetIsNonMemberBlockedToBuy(data.isBlockedToBuy);
        setBackdrop(false);
      });
  };

  useEffect(() => {
    if (auth?.user) {
      navigate("/showcase");
    } else {
      handleBlockedToBuy();
    }
  }, []);

  if (backdrop)
    return (
      <Backdrop sx={{ color: "primary.main" }} open={backdrop}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );

  return (
    <Container sx={{ py: 0 }}>
      <Grid container alignItems="center" spacing={{ xs: 0, lg: 5 }}>
        <Grid xs={12} lg={6}>
          <form onSubmit={onSubmit}>
            <Stack
              direction="column"
              justifyContent="center"
              alignItems="start"
              spacing={1}
              sx={{ maxWidth: "100%", height: { xs: "auto", lg: "85vh" }, py: { xs: 5, lg: 0 } }}
            >
              <Box
                component="img"
                src="/assets/americarn/Logo.webp"
                alt="Armadura do Dragão"
                sx={{ width: { xs: 180, lg: 220 }, cursor: "pointer" }}
              />
              <br />
              <Typography variant="h2" sx={{ fontWeight: 700, color: "white" }}>
                Olá, torcedor!
              </Typography>
              <Typography variant="h4" sx={{ color: "white" }}>
                Vamos iniciar a sua compra?
              </Typography>
              {!e2eAuth && login && (
                <Typography variant="body1" sx={{ color: "white" }}>
                  Prezado(a) Sócio(a), por favor, para iniciar a sua compra,
                  insira seu usuário e senha do Sócio Mecão de Coração.
                </Typography>
              )}
              <br />
              {!login && (
                <Stack spacing={2} sx={{ width: "100%" }}>
                  <Button
                    fullWidth
                    size="large"
                    variant="contained"
                    sx={{ minWidth: 200 }}
                    onClick={() => {
                      setLogin(true);
                    }}
                  >
                    Já sou sócio torcedor
                  </Button>
                  {!isNonMemberBlockedToBuy && (
                    <Button
                      fullWidth
                      size="large"
                      color="milky"
                      variant="outlined"
                      sx={{ minWidth: 200 }}
                      onClick={() => {
                        navigate("showcase");
                      }}
                    >
                      Não sou sócio torcedor
                    </Button>
                  )}
                </Stack>
              )}
              {login && (
                <>
                  <TextField
                    fullWidth
                    name="username"
                    label="Usuário"
                    variant="outlined"
                    color="primary"
                  />
                  <FormControl sx={{ m: 1, width: "100%" }} variant="outlined">
                    <InputLabel htmlFor="outlined-adornment-password">
                      Senha
                    </InputLabel>
                    <OutlinedInput
                      name="password"
                      type={showPassword ? "text" : "password"}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showPassword ? <EyeSlash /> : <Eye />}
                          </IconButton>
                        </InputAdornment>
                      }
                      label="Senha"
                    />
                  </FormControl>
                  <Grid container alignItems="center" sx={{ width: "100%" }}>
                    <Grid xs={12} lg={6}>
                      <Button
                        fullWidth
                        size="large"
                        variant="contained"
                        sx={{ minWidth: 200 }}
                        type="submit"
                      >
                        {progress && (
                          <CircularProgress color="black" size={24} />
                        )}
                        {!progress && "Entrar"}
                      </Button>
                    </Grid>
                    <Grid xs={12} lg={6} sx={{ textAlign: "right", p: 2 }}>
                      <Link
                        onClick={() => {
                          if (e2eAuth) {
                            navigate("/forgot");
                          } else {
                            modal?.open("Esqueceu a senha?", <ForgotModal />);
                          }
                        }}
                        sx={{ cursor: "pointer" }}
                      >
                        Esqueci minha senha.
                      </Link>
                    </Grid>
                  </Grid>
                </>
              )}
            </Stack>
          </form>
        </Grid>
        <Grid xs={12} lg={6}>
          <Box
            onClick={() => window.open("https://socio-americadenatal.futebolcard.com/")}
            component="img"
            src="/assets/americarn/banner-socio.webp"
            alt="Sócio Mecão"
            sx={{
              width: "100%",
              cursor: "pointer",
              border: "2px solid #FFF",
              borderRadius: 5,
            }}
          />
        </Grid>
      </Grid>
      <Modal />
      <Snackbar />
    </Container>
  );
};

Auth.defaultProps = {
  e2eAuth: false,
};

export default Auth;
